/**
 * ENG-271: what the drone panel shows for the ArduPilot conference.
 *
 * Every item below was reviewed with Lindsay (ENG-268 / ENG-269 PDFs). Items
 * that are fake, blank or can't be connected to real backend data are hidden,
 * not deleted — flip a flag to `true` to bring it back. Nothing else changes.
 *
 * "Surveillance tab" here is the tab inside the drone panel, not the
 * Surveillance context in the bottom nav (that one is public, ENG-279).
 */

/** Drone-panel tabs hidden for now ("Kill this tab for now"). Ids match `TabId`. */
export const HIDDEN_DRONE_TABS: readonly string[] = [
  'ldt-daa', // Surveillance
  'ldt-c2', // C2
  'ldt-proximity', // Proximity
  'ldt-flight', // Flight Ops
];

/** The tab to land on when a hidden tab is asked for (saved tab, alert link). */
export const FALLBACK_DRONE_TAB = 'ldt-status';

export const isDroneTabHidden = (id: string): boolean => HIDDEN_DRONE_TABS.includes(id);

/** The tab that should actually be shown/selected for a requested or saved one. */
export const visibleDroneTab = <T extends string>(id: T): T =>
  isDroneTabHidden(id) ? (FALLBACK_DRONE_TAB as T) : id;

export const droneContextFlags = {
  overview: {
    /** The old card layout for Aircraft (replaced by the key/value table). */
    cardLayout: false,
    /** Ops Status + Surveillance tiles: no backend source. */
    statusTiles: false,
    /** Flight Time (counts from the scheduled start, not real flight time). */
    flightTime: false,
    /** Battery: the feed sends none for this aircraft. */
    battery: false,
    /** ATL: not sent. */
    atl: false,
    /** C2 Links: no C2 feed yet ("Kill it"). */
    c2Links: false,
    /** ADS-B Out / In / TIS-B tiles ("Kill it"). */
    electronicConspicuity: false,
    /** Remote ID Operator ID ("Remove"). */
    operatorId: false,
    /** Operational intent Legal Rule ("Remove"). */
    legalRule: false,
    /** Force Satellites / HDOP / GPS Lock on. Off = they appear once satellites are
     *  known, i.e. once the raw GCS stream is linked (otherwise they are guesses). */
    navigation: false,
  },
  telemetry: {
    /** Battery row. */
    battery: false,
    /** Link RSSI row. */
    linkRssi: false,
  },
  terrain: {
    /** GPS-vs-Baro "within tolerance, RTM validated" warning (static text). */
    deltaWarning: false,
    /** Elevation Profile (terrain endpoints not implemented yet). */
    elevationProfile: false,
    /** Terrain cross-section (placeholder only). */
    crossSection: false,
    /** "OI Volume Submitted to RTM" accordion. */
    oiVolume: false,
    /** Elevation Data Sources accordion. */
    dataSources: false,
  },
  weather: {
    /** Forecast time slider (no forecast data). */
    forecast: false,
    /** The whole Temperature tile (it was boxed red with its feels-like line). */
    temperature: false,
    /** Feels-like line under the temperature (only matters if the tile is back on). */
    feelsLike: false,
    /** Visibility tile. */
    visibility: false,
    /** Conditions row. */
    conditions: false,
    /** Gusts tile. */
    gusts: false,
  },
  agents: {
    /** Cloud Processing card (no pipeline data). */
    cloudProcessing: false,
    /** YAML Save / Restart controls (backend endpoints not implemented yet). */
    yamlEditor: false,
  },
  /** Make/Model line in the map callouts ("remove Make/Model"). */
  calloutMakeModel: false,
} as const;
